import { EJourneys } from '@/context/display';
import { store } from '@/context/store';
import { EParameters, getParameters } from '@/utils/setParametersFromParams';

import { lookupNvic } from '../lookupNvic';
import { lookupVin } from '../lookupVin';

export async function lookupVehicle(partnerId: string): Promise<void> {
    const state = store.getState();
    const vehicle = state.borrowerReducer.vehicle;
    const { vin, nvic } = vehicle;
    const userFlow = state.displayReducer.partner.userFlow;
    let initialJourney = state.displayReducer.initialJourney;
    const testDrives = state.borrowerReducer.testDrives;
    const currentTestDriveIndex = testDrives?.currentTestDriveIndex ?? 0;
    const proceededToFinance =
        testDrives?.testDrives?.[currentTestDriveIndex]?.proceededToFinance;

    // If initialJourney is not set in Redux yet (e.g., during initial load),
    // check URL parameters directly as a fallback
    if (!initialJourney || initialJourney === EJourneys.FINANCE) {
        const params = getParameters();
        const paramJourney =
            params.get(EParameters.INITIAL_JOURNEY) || params.get('journey');
        if (
            paramJourney?.toLowerCase() === 'test_drive' ||
            paramJourney?.toUpperCase() === 'TEST_DRIVE'
        ) {
            initialJourney = EJourneys.TEST_DRIVE;
        }
    }

    // Only perform VIN lookup for test drive flow
    // For regular finance journey, VIN lookup is not performed (maintains original behavior)
    const isTestDriveFlow =
        userFlow === 'TEST_DRIVE' || initialJourney === EJourneys.TEST_DRIVE;

    if (proceededToFinance && !isTestDriveFlow) {
        return;
    }

    // Skip lookup if there is no NVIC or VIN and partnerId is UNKNOWN or missing
    // But allow for test drive flow with basic vehicle info
    if (
        !vin &&
        !nvic &&
        (!partnerId || partnerId === 'UNKNOWN') &&
        !isTestDriveFlow
    )
        return;

    // Only perform VIN/NVIC lookup for test drive flow
    if (!isTestDriveFlow) {
        // For regular finance journey, don't do VIN lookup (original behavior)
        return;
    }

    // For test drive flow, check if we already have vehicle data from previous lookup
    // If we have description or glassValuation, it means we've already done a lookup
    const hasExistingVehicleData =
        vehicle.description || vehicle.glassValuation;

    // If we already have complete vehicle data (description or glassValuation),
    // and we have a VIN in Redux, skip lookup to save costs
    if (hasExistingVehicleData && vehicle.vin) {
        return;
    }

    // If we have NVIC and glassValuation, skip lookup (NVIC lookup already done)
    if (vehicle.nvic && vehicle.glassValuation) {
        return;
    }

    // For test drive flow, perform lookup only if we don't have existing data
    // We prefer to use NVIC over VIN
    if (nvic) {
        await lookupNvic(partnerId);
        const { glassValuation } = store.getState().borrowerReducer.vehicle;

        // If glassValuation is set, then we have a valid vehicle and VIN is not required
        if (glassValuation) {
            return;
        }
        // If NVIC lookup didn't return glassValuation but we have a VIN, try VIN lookup
        if (vin) {
            const vinLookupSuccess = await lookupVin(partnerId);
            if (vinLookupSuccess) {
                return;
            }
        }
    } else if (vin) {
        // If we have a VIN but no NVIC, try VIN lookup (only for test drive flow)
        const vinLookupSuccess = await lookupVin(partnerId);
        // If VIN lookup was successful, we're done
        if (vinLookupSuccess) {
            return;
        }
    }

    // For test drive flow, if we have basic vehicle info but no lookup, still mark as known
    if (isTestDriveFlow) {
        if (vehicle.make || vehicle.model || vehicle.buildYear) {
            // Vehicle is known from URL parameters
            return;
        }
    }

    return;
}
